/**
 * KimiCanvas - Kimi-style canvas with selection glow and floating toolbar
 * 
 * Exact Kimi UI parity:
 * - Grid background
 * - Orange glow selection (not boxed borders)
 * - Floating toolbar on selected section (edit, duplicate, more)
 * - Section labels on selected elements
 * - No visible section containers when not selected
 */
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Pencil, Copy, MoreHorizontal, GripVertical, Trash2 } from 'lucide-react';

// Floating toolbar that appears on selected section
const FloatingToolbar = ({ rect, onEdit, onDuplicate, onDelete }) => {
  if (!rect) return null;
  
  return createPortal(
    <div
      className="fixed z-[9999] flex items-center gap-0.5 bg-zinc-900/95 backdrop-blur-sm rounded-md border border-zinc-700/50 p-0.5 shadow-lg"
      style={{
        top: rect.top + 8,
        left: rect.right - 100,
      }}
    >
      <button
        onClick={onEdit}
        className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded transition-colors"
        title="Edit"
      >
        <Pencil className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={onDuplicate}
        className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded transition-colors"
        title="Duplicate"
      >
        <Copy className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={onDelete}
        className="p-1.5 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
        title="Delete"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>,
    document.body
  );
};

// Section label pill
const SectionLabel = ({ rect, label }) => {
  if (!rect) return null;
  
  return createPortal(
    <div
      className="fixed z-[9998] px-2 py-1 bg-zinc-900/90 backdrop-blur-sm text-[10px] text-zinc-400 rounded border border-zinc-700/50"
      style={{
        bottom: window.innerHeight - rect.bottom + 8,
        right: window.innerWidth - rect.right + 8,
      }}
    >
      {label}
    </div>,
    document.body
  );
};

// Selection glow overlay
const SelectionGlow = ({ rect }) => {
  if (!rect) return null;
  
  return createPortal(
    <div
      className="fixed pointer-events-none z-[9997]"
      style={{
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      }}
    >
      {/* Orange glow border */}
      <div 
        className="absolute inset-0"
        style={{
          boxShadow: '0 0 0 2px rgba(249, 115, 22, 0.8), 0 0 20px rgba(249, 115, 22, 0.3)',
          borderRadius: '4px',
        }}
      />
    </div>,
    document.body
  );
};

// Kimi-style section wrapper (minimal, selection-based)
export const KimiSectionWrapper = ({
  sectionId,
  sectionLabel,
  isSelected,
  isHovered,
  onSelect,
  onEdit,
  onDuplicate,
  onDelete,
  children,
}) => {
  const wrapperRef = useRef(null);
  const [rect, setRect] = useState(null);
  
  // Track element rect
  useEffect(() => {
    const updateRect = () => {
      if (wrapperRef.current) {
        const r = wrapperRef.current.getBoundingClientRect();
        setRect({
          top: r.top,
          left: r.left,
          right: r.right,
          bottom: r.bottom,
          width: r.width,
          height: r.height,
        });
      }
    };
    
    updateRect();
    
    // Update on scroll/resize
    window.addEventListener('scroll', updateRect, { passive: true });
    window.addEventListener('resize', updateRect);
    
    // Update on any scroll within parents
    let parent = wrapperRef.current?.parentElement;
    while (parent) {
      parent.addEventListener('scroll', updateRect, { passive: true });
      parent = parent.parentElement;
    }
    
    return () => {
      window.removeEventListener('scroll', updateRect);
      window.removeEventListener('resize', updateRect);
    };
  }, []);
  
  return (
    <>
      <div
        ref={wrapperRef}
        data-block-id={sectionId}
        data-block-label={sectionLabel}
        className="relative"
        onClick={(e) => {
          e.stopPropagation();
          onSelect(sectionId);
        }}
        data-testid={`kimi-section-${sectionId}`}
      >
        {children}
      </div>
      
      {/* Selection overlays - rendered via portal */}
      {isSelected && rect && (
        <>
          <SelectionGlow rect={rect} />
          <FloatingToolbar 
            rect={rect} 
            onEdit={onEdit}
            onDuplicate={() => onDuplicate(sectionId)}
            onDelete={() => onDelete(sectionId)}
          />
          <SectionLabel rect={rect} label={sectionLabel} />
        </>
      )}
    </>
  );
};

// Canvas container with grid background
// forwardRef exposes the inner scroll container so parent can preserve scrollTop
const KimiCanvas = React.forwardRef(({
  children,
  onCanvasClick,
  viewport = 'desktop',
}, ref) => {
  // Viewport widths
  const viewportWidths = {
    desktop: '100%',
    tablet: '768px',
    mobile: '390px',
  };
  
  return (
    <div 
      className="flex-1 bg-zinc-900/50 overflow-hidden flex justify-center items-start p-4"
      onClick={onCanvasClick}
      data-testid="kimi-canvas"
      style={{
        // Subtle grid pattern like Kimi
        backgroundImage: `
          linear-gradient(rgba(63, 63, 70, 0.1) 1px, transparent 1px),
          linear-gradient(90deg, rgba(63, 63, 70, 0.1) 1px, transparent 1px)
        `,
        backgroundSize: '20px 20px',
      }}
    >
      {/* Canvas frame */}
      <div
        className="bg-black rounded-lg overflow-hidden shadow-2xl"
        style={{
          width: viewportWidths[viewport],
          maxWidth: '100%',
          height: 'calc(100vh - 80px)',
        }}
      >
        {/* Scrollable content */}
        <div 
          ref={ref}
          className="h-full overflow-y-auto overflow-x-hidden"
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(113, 113, 122, 0.3) transparent',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
});

KimiCanvas.displayName = 'KimiCanvas';

export default KimiCanvas;
